/**
 * DiffViewer — GitHub-style unified-diff renderer (CR-D §3.6).
 *
 * Renders a unified diff string (the `diff` field returned by the chat + commit-request
 * endpoints) with per-line colouring and two-sided line numbers derived from the hunk
 * headers. Kept as a dependency-free renderer: the diff string is already unified, so
 * there is nothing to compute client-side.
 *
 * Used by DiffProposal (chat tab) and CommitRequestPanel (approval flow).
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';

export interface DiffViewerProps {
  /** Unified diff string (e.g. `@@ -1,3 +1,4 @@\n context\n-old\n+new`). */
  diff: string;
  className?: string;
}

type DiffLineKind = 'add' | 'del' | 'context' | 'hunk' | 'meta';

interface DiffLine {
  kind: DiffLineKind;
  text: string;
  oldNo: number | null;
  newNo: number | null;
}

const HUNK_HEADER = /^@@\s+-(\d+)(?:,\d+)?\s+\+(\d+)(?:,\d+)?\s+@@/;

/** Parse a unified diff string into rows with reconstructed old/new line numbers. */
function parseDiff(diff: string): DiffLine[] {
  const rows: DiffLine[] = [];
  let oldNo = 0;
  let newNo = 0;

  for (const raw of diff.replace(/\r\n/g, '\n').split('\n')) {
    const hunk = HUNK_HEADER.exec(raw);
    if (hunk) {
      oldNo = Number(hunk[1]);
      newNo = Number(hunk[2]);
      rows.push({ kind: 'hunk', text: raw, oldNo: null, newNo: null });
      continue;
    }

    // File-header / metadata lines emitted by unified diff tooling.
    if (/^(diff |index |---|\+\+\+|new file|deleted file|similarity |rename )/.test(raw)) {
      rows.push({ kind: 'meta', text: raw, oldNo: null, newNo: null });
      continue;
    }

    if (raw.startsWith('+')) {
      rows.push({ kind: 'add', text: raw.slice(1), oldNo: null, newNo: newNo++ });
      continue;
    }
    if (raw.startsWith('-')) {
      rows.push({ kind: 'del', text: raw.slice(1), oldNo: oldNo++, newNo: null });
      continue;
    }
    // Context line (leading space) or bare line.
    const text = raw.startsWith(' ') ? raw.slice(1) : raw;
    rows.push({ kind: 'context', text, oldNo: oldNo++, newNo: newNo++ });
  }

  return rows;
}

const ROW_STYLES: Record<DiffLineKind, string> = {
  add: 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300',
  del: 'bg-red-500/10 text-red-800 dark:text-red-300',
  context: 'text-text-primary',
  hunk: 'bg-blue-500/10 text-blue-800 dark:text-blue-300',
  meta: 'text-text-secondary',
};

const PREFIX: Record<DiffLineKind, string> = {
  add: '+',
  del: '-',
  context: ' ',
  hunk: '',
  meta: '',
};

/** Render a unified diff string as a colourised, line-numbered table. */
export function DiffViewer({ diff, className }: DiffViewerProps): JSX.Element {
  const rows = useMemo(() => parseDiff(diff), [diff]);

  if (rows.length === 0) {
    return (
      <p className={cn('text-sm text-text-secondary', className)}>No changes to display.</p>
    );
  }

  return (
    <div
      className={cn(
        'overflow-x-auto rounded-md border border-outlines bg-card font-mono text-xs leading-relaxed',
        className,
      )}
      role="table"
      aria-label="File diff"
    >
      {rows.map((row, idx) => (
        <div key={idx} role="row" className={cn('flex', ROW_STYLES[row.kind])}>
          <span
            className="w-10 shrink-0 select-none border-r border-outlines/50 px-2 text-right text-text-secondary"
            aria-hidden="true"
          >
            {row.oldNo ?? ''}
          </span>
          <span
            className="w-10 shrink-0 select-none border-r border-outlines/50 px-2 text-right text-text-secondary"
            aria-hidden="true"
          >
            {row.newNo ?? ''}
          </span>
          <pre className="m-0 flex-1 whitespace-pre-wrap break-words px-2">
            {PREFIX[row.kind]}
            {row.text}
          </pre>
        </div>
      ))}
    </div>
  );
}

export default DiffViewer;
